import { Controller } from "@hotwired/stimulus"

/**
 * JAP1 Stats Controller
 * Handles the statistics page for tracking learning progress
 */
export default class extends Controller {
  static targets = [
    "questionsSeen",
    "questionsMastered",
    "questionsToReview",
    "masteredPercent",
    "studyTime",
    "chapitresProgress",
    "examsHistory"
  ]

  connect() {
    this.loadAndDisplayStats()
  }

  // Load stats from localStorage and display
  loadAndDisplayStats() {
    const progress = this.getProgress()

    // Calculate totals
    const stats = this.calculateStats(progress)

    // Update UI
    this.updateStatsDisplay(stats)
    this.updateChapitresDisplay(progress)
    this.updateExamsDisplay(progress)
  }

  // Get progress from localStorage
  getProgress() {
    const stored = localStorage.getItem("jap1_progress")
    if (stored) {
      try {
        return JSON.parse(stored)
      } catch (e) {
        console.error("Error parsing progress:", e)
      }
    }
    return { chapitres: {}, stats: {}, examens: [], toReview: [] }
  }

  // Calculate statistics
  calculateStats(progress) {
    let questionsSeen = 0
    let questionsMastered = 0
    const totalQuestions = 254

    Object.values(progress.chapitres || {}).forEach(chapitre => {
      if (chapitre.seen) questionsSeen += chapitre.seen.length
      if (chapitre.mastered) questionsMastered += chapitre.mastered.length
    })

    const questionsToReview = progress.toReview?.length || 0
    const masteredPercent = Math.round((questionsMastered / totalQuestions) * 100)
    const studyTime = progress.stats?.studyTime || 0

    return {
      questionsSeen,
      questionsMastered,
      questionsToReview,
      masteredPercent,
      studyTime
    }
  }

  // Update main stats display
  updateStatsDisplay(stats) {
    if (this.hasQuestionsSeenTarget) {
      this.questionsSeenTarget.textContent = stats.questionsSeen
    }

    if (this.hasQuestionsMasteredTarget) {
      this.questionsMasteredTarget.textContent = stats.questionsMastered
    }

    if (this.hasQuestionsToReviewTarget) {
      this.questionsToReviewTarget.textContent = stats.questionsToReview
    }

    if (this.hasMasteredPercentTarget) {
      this.masteredPercentTarget.textContent = `${stats.masteredPercent}%`
    }

    if (this.hasStudyTimeTarget) {
      const hours = Math.floor(stats.studyTime / 3600)
      const minutes = Math.floor((stats.studyTime % 3600) / 60)
      this.studyTimeTarget.textContent = hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`
    }
  }

  // Update per-chapitre progress display
  updateChapitresDisplay(progress) {
    if (!this.hasChapitresProgressTarget) return

    const questionsPerChapitre = {
      1: 12, 2: 8, 3: 17, 4: 11, 5: 9, 6: 8, 7: 5, 8: 25,
      9: 7, 10: 48, 11: 18, 12: 12, 13: 53, 14: 17, 15: 4
    }

    const rows = this.chapitresProgressTarget.querySelectorAll("div")
    rows.forEach((row, index) => {
      const chapitreId = index + 1
      const total = questionsPerChapitre[chapitreId] || 10
      const chapitre = progress.chapitres?.[chapitreId] || { mastered: [] }
      const mastered = chapitre.mastered?.length || 0
      const percent = Math.round((mastered / total) * 100)

      const bar = row.querySelector("div > div")
      const percentSpan = row.querySelector("span:last-child")

      if (bar) {
        bar.style.width = `${percent}%`

        // Color based on completion
        bar.classList.remove("bg-[#0055A4]", "bg-green-500", "bg-yellow-500")
        if (percent >= 80) {
          bar.classList.add("bg-green-500")
        } else if (percent >= 40) {
          bar.classList.add("bg-[#0055A4]")
        } else {
          bar.classList.add("bg-yellow-500")
        }
      }

      if (percentSpan) {
        percentSpan.textContent = `${percent}%`
      }
    })
  }

  // Update exams history display
  updateExamsDisplay(progress) {
    if (!this.hasExamsHistoryTarget) return

    const examens = progress.examens || []

    if (examens.length === 0) {
      return // Keep placeholder message
    }

    // Build exam history HTML
    const html = examens.slice(-5).reverse().map(exam => {
      const date = new Date(exam.date)
      const formattedDate = date.toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "short",
        year: "numeric"
      })

      const score = exam.score
      const percent = Math.round((score.correct / score.total) * 100)
      const duration = Math.floor(exam.duration / 60)

      const colorClass = percent >= 80 ? "text-green-600" : percent >= 50 ? "text-yellow-600" : "text-red-600"

      return `
        <div class="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
          <div>
            <div class="font-medium text-gray-900">${exam.type === "theorique" ? "Examen théorique" : "QCM rapide"}</div>
            <div class="text-sm text-gray-500">${formattedDate} • ${duration} min</div>
          </div>
          <div class="text-right">
            <div class="font-bold ${colorClass}">${percent}%</div>
            <div class="text-sm text-gray-500">${score.correct}/${score.total}</div>
          </div>
        </div>
      `
    }).join("")

    this.examsHistoryTarget.innerHTML = html
  }

  // Export progress as JSON file
  exportProgress() {
    const progress = this.getProgress()
    const data = {
      version: "1.0",
      exportDate: new Date().toISOString(),
      progress
    }

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" })
    const url = URL.createObjectURL(blob)

    const a = document.createElement("a")
    a.href = url
    a.download = `jap1_progress_${new Date().toISOString().split("T")[0]}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Import progress from JSON file
  importProgress(event) {
    const file = event.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result)
        if (data.progress) {
          localStorage.setItem("jap1_progress", JSON.stringify(data.progress))
          this.loadAndDisplayStats()
          alert("Progression importée avec succès !")
        } else {
          alert("Fichier invalide : pas de données de progression trouvées")
        }
      } catch (error) {
        console.error("Error importing:", error)
        alert("Erreur lors de l'import du fichier")
      }
    }
    reader.readAsText(file)
  }

  // Reset all progress
  resetProgress() {
    if (confirm("Es-tu sûr de vouloir effacer toute ta progression ? Cette action est irréversible.")) {
      localStorage.removeItem("jap1_progress")
      this.loadAndDisplayStats()
      alert("Progression réinitialisée")
    }
  }
}
